import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useQuery, useSubscription } from '@apollo/client';
import {
  MY_NOTIFICATIONS,
  NOTIFICATION_COUNT,
  NOTIFICATIONS_CHANGED,
  MARK_NOTIFICATIONS_READ,
  MARK_ALL_NOTIFICATIONS_READ
} from 'src/graphQL/notifications';

export default function useNotificationInbox(open) {
  const client = useApolloClient();
  const {
    data,
    error: countError,
    refetch: refetchCount
  } = useQuery(NOTIFICATION_COUNT, { fetchPolicy: 'network-only' });
  const [page, setPage] = useState({ items: [], nextCursor: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const request = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
      request.current += 1;
    },
    []
  );

  const load = useCallback(
    async (cursor = null) => {
      const generation = ++request.current;
      setLoading(true);
      setError(null);
      try {
        const result = await client.query({
          query: MY_NOTIFICATIONS,
          variables: { cursor, limit: 20 },
          fetchPolicy: 'no-cache'
        });
        if (!mounted.current || generation !== request.current) return;
        const next = result.data.myNotifications;
        setPage((previous) => ({
          ...next,
          items: cursor
            ? [
                ...new Map(
                  [...previous.items, ...next.items].map((item) => [
                    item.id,
                    item
                  ])
                ).values()
              ]
            : next.items
        }));
        client.writeQuery({
          query: NOTIFICATION_COUNT,
          data: { myNotificationsUnreadCount: next.unreadCount }
        });
      } catch (caught) {
        if (mounted.current && generation === request.current) setError(caught);
      } finally {
        if (mounted.current && generation === request.current)
          setLoading(false);
      }
    },
    [client]
  );

  const refresh = useCallback(() => {
    refetchCount().catch(() => {});
    if (open) load();
  }, [refetchCount, load, open]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const refreshTimer = useRef(null);
  const { error: realtimeError } = useSubscription(NOTIFICATIONS_CHANGED, {
    onSubscriptionData: () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => refreshRef.current(), 150);
    }
  });

  useEffect(() => {
    if (open) load();
  }, [open, load]);
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== 'hidden') refreshRef.current();
    };
    window.addEventListener('focus', sync);
    window.addEventListener('online', sync);
    window.addEventListener('universe:realtime-connected', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      clearTimeout(refreshTimer.current);
      window.removeEventListener('focus', sync);
      window.removeEventListener('online', sync);
      window.removeEventListener('universe:realtime-connected', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const markRead = async (id) => {
    setError(null);
    try {
      await client.mutate({
        mutation: id ? MARK_NOTIFICATIONS_READ : MARK_ALL_NOTIFICATIONS_READ,
        variables: id ? { ids: [id] } : {}
      });
      refresh();
    } catch (caught) {
      setError(caught);
    }
  };

  return {
    ...page,
    count: data?.myNotificationsUnreadCount || 0,
    loading,
    error: error || countError,
    realtimeError,
    load,
    markRead,
    refresh
  };
}
